import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import useLayoutHistory from '../useLayoutHistory/useLayoutHistory'
import usePreviewViewMode from '../usePreviewViewMode/usePreviewViewMode'
import {
  addBlockToLayouts,
  buildLayoutsFromSavedLayout,
  cloneLayouts,
  createBlockId,
  haveLayoutsChanged,
  keepLayoutItemHeights,
  keepLayoutsItemHeights,
  removeBlockFromLayouts,
  updateLayoutItemHeights,
} from './helpers'

const useTrackingPageLayout = ({ savedLayout }) => {
  const initialLayouts = useMemo(() => savedLayout && buildLayoutsFromSavedLayout(savedLayout), [savedLayout])
  const [layouts, setLayouts] = useState(null)
  const pendingBlockHeightsRef = useRef(null)

  const { viewMode, viewModeOptions, isMobileView, setViewMode } = usePreviewViewMode({
    onChange: previousViewMode => history.recordViewModeChange(previousViewMode),
  })

  const history = useLayoutHistory({ layouts, viewMode, setLayouts, setViewMode })

  useEffect(() => {
    if (initialLayouts) setLayouts(previousLayouts => keepLayoutsItemHeights(initialLayouts, previousLayouts))
  }, [initialLayouts])

  const layout = layouts?.[viewMode]
  const hasLayoutChanges = Boolean(layouts) && haveLayoutsChanged(layouts, initialLayouts)

  const handleLayoutChange = useCallback(
    newLayout => {
      setLayouts(previousLayouts => {
        return { ...previousLayouts, [viewMode]: keepLayoutItemHeights(newLayout, previousLayouts[viewMode]) }
      })
    },
    [viewMode]
  )

  const applyBlockHeights = blockHeights => {
    setLayouts(previousLayouts => {
      const activeLayout = previousLayouts[viewMode]
      const updatedLayout = updateLayoutItemHeights(activeLayout, blockHeights)

      if (updatedLayout === activeLayout) return previousLayouts

      return { ...previousLayouts, [viewMode]: updatedLayout }
    })
  }

  const handleLayoutChangeStart = () => {
    pendingBlockHeightsRef.current = {}
    history.beginLayoutChange(cloneLayouts(layouts))
  }

  const handleLayoutChangeCommit = newLayout => {
    const pendingBlockHeights = pendingBlockHeightsRef.current

    pendingBlockHeightsRef.current = null

    history.commitLayoutChange(pendingLayouts => {
      return haveLayoutsChanged({ ...pendingLayouts, [viewMode]: newLayout }, pendingLayouts)
    })

    if (pendingBlockHeights) applyBlockHeights(pendingBlockHeights)
  }

  const handleBlockHeightChange = (blockId, height) => {
    if (pendingBlockHeightsRef.current) {
      pendingBlockHeightsRef.current[blockId] = height

      return
    }

    applyBlockHeights({ [blockId]: height })
  }

  const addBlock = blockId => {
    const newBlockId = createBlockId(blockId)

    history.recordLayoutChange(cloneLayouts(layouts))
    setLayouts(previousLayouts => addBlockToLayouts(previousLayouts, newBlockId))

    return newBlockId
  }

  const removeBlock = blockId => {
    history.recordLayoutChange(cloneLayouts(layouts))
    setLayouts(previousLayouts => removeBlockFromLayouts(previousLayouts, blockId))
  }

  return {
    viewMode,
    viewModeOptions,
    isMobileView,
    layouts,
    layout,
    hasLayoutChanges,
    handleLayoutChange,
    handleLayoutChangeStart,
    handleLayoutChangeCommit,
    handleBlockHeightChange,
    addBlock,
    removeBlock,
    canUndo: history.canUndo,
    handleUndo: history.undo,
    canRedo: history.canRedo,
    handleRedo: history.redo,
  }
}

export default useTrackingPageLayout
